/* eslint-disable no-console */
import {Divider, Col, Flex} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import StakeholderTrackingBg from '../../../../../assets/skeleton/StakeholderTrackingBg.png';
import BlockAccessPage from '../../../../../components/BlockAccess/BlockAccessPage';
import Loader from '../../../../../components/Loader/Loader';
import PageContent from '../../../../../components/PageContent/PageContent';
import sumTableValue from '../../../../../components/Table/Utils/sumTableValue';
import {APITypeEnum} from '../../../../../services/interface/tableAPIType';
import stakeholderTypeAPI from '../../../../../services/modules/StakeholderTracking/stakeholderTypeAPI';
import chartData from '../../../../../staticJson/stakeholder.json';
import useCompanyStore, {
  accessModule
} from '../../../../../store/companyStore/companyDetailsStore';
import {checkCurrentYearOrBelow} from '../../../../../utils/commonFunctions';
// import {yearSelect} from '../../../../../utils/FormUtils/yearSelect';
import {shouldBlockModuleAccess} from '../../../../../utils/SubscriptionHelper';
import {roundToDecimal} from '../../../../netZero/constants/commonFunctions';
import {stakeholdersTypeKeyLabel} from '../constants/common';

// import stakeHolderData from './../../../../../staticJson/stakeHolderTracking.json';
import ActionItems from './ActionItems';
import InfoTable from './InfoTable';
import StakeHolderCharts from './StakeHolderCharts';
import StakeholderFilterComp from './StakeholderFilterComp';

const StakeholderTracking: React.FC = () => {
  const [progressFilter, setProgressFilter] = useState<any | undefined>();
  const [mainStakeHolderData, setMainStackHolderData] = useState<any[]>([]);
  const [searchParam, setSearchParams] = useSearchParams();
  const [selectedYear, setSelectedYear] = useState('');
  const [showDetails, setShowDetails] = useState<any[]>([]);
  const [stakeholderType, setStakeholderType] = useState('facility');
  const [progressChartData, setProgressChartData] = useState<any>({
    value: 0,
    unit: '%',
    subData: chartData
  });
  const [isLoading, setIsLoading] = useState(true);

  const companyId = useCompanyStore.getState().companyId;

  // const firstYear = yearSelect();

  useEffect(() => {
    const currentYear = checkCurrentYearOrBelow(
      searchParam.get('selectedYear')
    );
    setSelectedYear(currentYear);
    searchParam.set('selectedYear', currentYear);
    setSearchParams(searchParam);
  }, []);

  const MainData = useMemo(() => {
    if (mainStakeHolderData.length > 0) {
      const totalPer = sumTableValue(
        mainStakeHolderData,
        'percentage',
        false
      ) as number;

      const totalAveragePer = totalPer / mainStakeHolderData.length;

      return {
        value: roundToDecimal(totalAveragePer, 0) ?? 0,
        unit: '%',
        subData: chartData.map((item) => {
          return {
            ...item,
            value:
              item.dataId === 'Filled' ? totalAveragePer : 100 - totalAveragePer
          };
        })
      };
    }
  }, [mainStakeHolderData]);

  useEffect(() => {
    if (!isLoading) {
      setProgressChartData(
        progressFilter
          ? {
              value: progressFilter?.percentage ?? 0,
              unit: '%',
              subData: chartData.map((item) => {
                return {
                  ...item,
                  value:
                    item.dataId === 'Filled'
                      ? progressFilter?.percentage
                      : 100 - progressFilter?.percentage
                };
              })
            }
          : MainData ?? {
              value: 0,
              unit: '%',
              subData: chartData
            }
      );
    }
    // }
  }, [progressFilter, MainData]);

  useEffect(() => {
    if (stakeholderType && selectedYear) {
      setIsLoading(true);
      Promise.all([
        stakeholderTypeAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${stakeholderType}/${companyId}/${selectedYear}`
        )
      ])
        .then(([mainData]) => {
          setMainStackHolderData(mainData ?? []);
          setShowDetails([]);
          setProgressFilter(undefined);
        })
        .catch(() => {})
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedYear, stakeholderType]);

  return (
    <PageContent>
      <div
        style={{
          color: '#024064',
          fontFamily: 'Montserrat',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: ' 120%',
          margin: '0 0 24px 32px'
        }}
      >
        Stakeholder Tracking
      </div>
      <Divider style={{border: '1px solid #D2D4DA'}} />

      {shouldBlockModuleAccess(accessModule.stakeholderTracking) ? (
        <Flex
          style={{
            width: '100%',
            backgroundColor: '#ffffff',
            border: '1px solid #F3F4F8',
            borderRadius: '12px',
            padding: '20px'
          }}
          gap={45}
        >
          <Flex
            vertical
            gap={25}
            style={{width: '100%', marginTop: 100, position: 'relative'}}
          >
            <BlockAccessPage backgroundImage={StakeholderTrackingBg} />
          </Flex>
        </Flex>
      ) : (
        <Flex gap={30} vertical style={{padding: '8px 32px 32px 32px'}}>
          <StakeholderFilterComp
            setSelectedYear={setSelectedYear}
            setStakeholderType={setStakeholderType}
            stakeholderType={stakeholderType}
            setProgressFilter={setProgressFilter}
            isDisabled={isLoading}
          />
          {isLoading && progressChartData ? (
            <Loader isHome />
          ) : (
            <>
              <Flex gap={10} style={{width: '100%', height: '280px'}}>
                <div
                  style={{
                    flex: 0.3,
                    borderRadius: '8px',
                    border: '1px solid #D2D4DA',
                    padding: '30px 30px 37px 37px'
                  }}
                >
                  <StakeHolderCharts
                    data={progressChartData}
                    subData={progressChartData?.subData}
                    label={
                      progressFilter
                        ? progressFilter?.name
                        : stakeholdersTypeKeyLabel?.find((item) => {
                            return item.value === stakeholderType;
                          })?.label ?? ''
                    }
                  />
                </div>
                <InfoTable
                  setShowDetails={setShowDetails}
                  dataSource={mainStakeHolderData}
                  stakeholderType={stakeholderType}
                  setProgressFilter={setProgressFilter}
                />
              </Flex>
              <div
                style={{
                  color: '#5B5D6B',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: ' 120%',
                  paddingLeft: '16px'
                }}
              >
                Action Items
              </div>
              <Col span={24}>
                <ActionItems
                  dataSource={showDetails?.length > 0 ? showDetails : []}
                />
              </Col>
            </>
          )}
        </Flex>
      )}
    </PageContent>
  );
};

export default StakeholderTracking;
